import { useCallback, useMemo, useRef, useState } from 'react';
import { ReactFlowProvider, useReactFlow } from '@xyflow/react';
import { useTranslation } from 'react-i18next';
import { ProcessFlowEditor, type CanvasNodeKind, type CursorMode } from '@bliks-io/ui';
import { useAuthStore } from '@/stores/auth-store';
import { useHistoryBack } from '@/components/back-button';
import {
  useCreateProcessDraft,
  useDiscardProcessDraft,
  usePublishDraft,
  useSetDraftResurveyAll
} from '@/api/mutations/useProcessDraft';
import { useDraftEditorData } from './useDraftEditorData';
import { useDraftPublishHandlers } from './useDraftPublishHandlers';
import { useDraftSaveStatus } from './useDraftSaveStatus';
import { DraftEditorToolbar } from './panels/DraftEditorToolbar';
import { DraftEditorBottomToolbar } from './panels/DraftEditorBottomToolbar';
import { MergeNodePickerDialog } from './panels/MergeNodePickerDialog';
import { DraftConflictDialog } from './panels/DraftConflictDialog';
import { DraftIssuesSidebar } from './panels/DraftIssuesSidebar';
import { EndlessProcessLoader } from '@/pages/processes/components/endless-process/EndlessProcessLoader';
import { DraftIssuesContext, useDraftIssues } from './useDraftIssues';
import { useDraftUndoControls } from './undo/useDraftUndoControls';
import { useUndoableEditorActions } from './actions/useUndoableEditorActions';
import { useSwimlaneActions } from './actions/useSwimlaneActions';
import { useEditorKeyboard } from './keyboard/useEditorKeyboard';
import { useNodeContextMenuHandlers } from './actions/useNodeContextMenuHandlers';
import { useDraftNodeDeletion } from './actions/useDraftNodeDeletion';
import { useDraftDetailRenderer } from './actions/useDraftDetailRenderer';
import { useAutoLayoutAction } from './actions/useAutoLayoutAction';
import { useDraftEditorContextMenu } from './actions/useDraftEditorContextMenu';
import { useStepTemplateActions } from './actions/useStepTemplateActions';
import { useAddStepHandlers } from './actions/useAddStepHandlers';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { useAssigneeLookup, useAssigneeLookupReady } from '@/lib/useAssigneeLookup';

interface Props {
  processId: string;
}

function DraftEditorInner({ processId }: Props) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const { screenToFlowPosition } = useReactFlow();

  const {
    draftQuery,
    layoutData,
    draftData,
    steps,
    draftAssignments,
    draftEvents,
    classifyContext,
    validation,
    stepNumberByStableId,
    displayedSteps,
    effectiveOpenAssignments,
    entitySuggestions,
    processForFlow,
    nodePositions,
    edgeWaypoints
  } = useDraftEditorData(processId);
  // The draft query has `retry: false`, so a SINGLE failed background refetch
  // (transient 429/5xx) immediately flips `isRefetchError` — flashing the
  // "changes not saved" banner on a one-off blip. Only treat the failure as
  // persistent once two or more refetches errored with no success in between;
  // any successful refetch resets the baseline.
  const errorCountAtLastSuccessRef = useRef(draftQuery.errorUpdateCount);
  if (!draftQuery.isRefetchError) {
    errorCountAtLastSuccessRef.current = draftQuery.errorUpdateCount;
  }
  const hasPersistentRefetchError =
    draftQuery.isRefetchError &&
    draftQuery.errorUpdateCount - errorCountAtLastSuccessRef.current >= 2;

  const createDraft = useCreateProcessDraft(processId);
  const discardDraft = useDiscardProcessDraft(processId);
  const publishDraft = usePublishDraft(processId);
  const reRequestAllSurveys = useSetDraftResurveyAll(processId);
  const saveStatus = useDraftSaveStatus(processId);

  // Draft assignments can reference not-yet-activated users / systems / external
  // contacts whose populated object never travels with the draft — the shared
  // lookup resolves their swimlane (and the detail panels' cards) instead of
  // falling back to "Unassigned" / a raw id.
  const assigneeLookup = useAssigneeLookup();
  const assigneeLookupReady = useAssigneeLookupReady();
  const undoControls = useDraftUndoControls(processId, draftData?.history);
  // Stable getter so referential identity is preserved across renders that
  // don't change the lookup payload — `useEdgeActionDeps` memoizes its deps
  // object on this reference.
  const assigneeLookupRef = useRef(assigneeLookup);
  assigneeLookupRef.current = assigneeLookup;
  const getAssigneeLookup = useCallback(() => assigneeLookupRef.current, []);
  const undoableActions = useUndoableEditorActions({
    processId,
    getAssigneeLookup
  });
  const swimlaneActions = useSwimlaneActions(undoableActions, layoutData?.laneColors);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleBack = useHistoryBack({
    to: '/$slug/processes/all-processes/$processId',
    params: { slug, processId }
  });

  const isCanvasReady = !!layoutData?.updatedAt && assigneeLookupReady;
  const { selectedStepId, setSelectedStepId, renderDetail } = useDraftDetailRenderer({
    processId,
    steps,
    draftAssignments,
    displayedSteps,
    draftEvents
  });
  const issues = useDraftIssues({
    draftId: draftData?.draft._id,
    validation,
    steps,
    draftAssignments,
    draftEvents,
    stepNumberByStableId,
    undoableActions,
    setSelectedStepId,
    containerRef,
    isCanvasReady
  });

  const {
    activeConflicts,
    conflictSource,
    handlePublish,
    handlePublishAnyway,
    handleDismissConflict,
    setConflictDismissed,
    isPublishPending
  } = useDraftPublishHandlers({
    onPublished: handleBack,
    publishDraft,
    loadTimeLiveConflict: draftData?.liveConflict,
    onValidationRejected: issues.handleValidationRejected
  });

  // Right-click "publish" action: stage a re-request on every step, then publish
  // — so all surveys are refreshed in the same publish rather than per step.
  const handlePublishAndReRequestAll = useCallback(async () => {
    try {
      await reRequestAllSurveys.mutateAsync({ value: true });
    } catch (error) {
      toastApiError(error, 'errors.unknown');
      return;
    }
    await handlePublish();
  }, [reRequestAllSurveys, handlePublish]);

  const handleDiscard = useCallback(() => {
    discardDraft.mutate(undefined, {
      onSuccess: () => {
        handleBack();
      }
    });
  }, [discardDraft, handleBack]);

  const isRecreatingDraft = discardDraft.isPending || createDraft.isPending;

  const handleDiscardAndRecreate = useCallback(() => {
    discardDraft.mutate(undefined, {
      onSuccess: () => {
        createDraft.mutate(undefined, {
          onSuccess: () => setConflictDismissed(true)
        });
      }
    });
  }, [discardDraft, createDraft, setConflictDismissed]);

  const { handleAddFirstStep, handleAddStepCenter } = useAddStepHandlers({
    containerRef,
    undoableActions,
    selectedStepId
  });

  const handleAddNoteCenter = useCallback(() => {
    const center = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2
    });
    void undoableActions.addNote({ x: center.x, y: center.y });
  }, [undoableActions, screenToFlowPosition]);

  const handleDeleteNodes = useDraftNodeDeletion(undoableActions);

  // Context-menu delete: one node, kind from the menu target's builder stamp.
  // Raw-id event nodes carry their event id as the node id, so no `eventId`
  // enrichment is needed on this path.
  const handleDeleteNode = useCallback(
    (nodeId: string, kind: CanvasNodeKind) => handleDeleteNodes([{ nodeId, kind }]),
    [handleDeleteNodes]
  );

  const [cursorMode, setCursorMode] = useState<CursorMode>('pan');
  useEditorKeyboard({
    containerRef,
    onUndo: undoControls.undo,
    onRedo: undoControls.redo,
    setCursorMode
  });

  // Picker open-state: the stableStepId of the right-clicked card the user is
  // merging FROM. Null = dialog closed.
  const [mergePickerSourceId, setMergePickerSourceId] = useState<string | null>(null);
  const handleOpenMergePicker = useCallback((nodeId: string) => setMergePickerSourceId(nodeId), []);

  const startEventIds = useMemo(
    () => new Set(draftEvents.filter((ev) => ev.eventKind === 'start').map((ev) => ev._id)),
    [draftEvents]
  );

  const nodeHandlers = useNodeContextMenuHandlers({
    undoableActions,
    steps,
    draftAssignments,
    startEventIds,
    onSelectStep: setSelectedStepId,
    onOpenMergePicker: handleOpenMergePicker
  });

  const handleAutoLayout = useAutoLayoutAction(processForFlow, undoableActions.onSavePositions);

  const stepTemplates = useStepTemplateActions({
    processId,
    steps,
    draftEvents,
    assigneeLookup
  });

  const {
    handlePaneContextMenu,
    handleNodeContextMenu,
    handleEdgeContextMenu,
    contextMenuWrapper
  } = useDraftEditorContextMenu({
    undoableActions,
    swimlaneActions,
    nodeHandlers,
    handleDeleteNode,
    classifyContext,
    ...stepTemplates.contextMenuProps
  });

  const bottomToolbar = (
    <DraftEditorBottomToolbar
      onAutoLayout={handleAutoLayout}
      onUndo={undoControls.undo}
      onRedo={undoControls.redo}
      canUndo={undoControls.canUndo}
      canRedo={undoControls.canRedo}
      cursorMode={cursorMode}
      onCursorModeChange={setCursorMode}
      onAddStepCenter={handleAddStepCenter}
      onAddNoteCenter={handleAddNoteCenter}
    />
  );

  if (draftQuery.isLoading || createDraft.isPending) {
    return <EndlessProcessLoader messageKey="processDraft.preparing" />;
  }

  // `isLoadingError` (not `isError`): only surface the fatal load error when
  // the INITIAL fetch failed with nothing to render. Every mutation's
  // settle-invalidation refetches the draft with `retry: false`; a single
  // failed background refetch (e.g. a transient 429/5xx mid-editing) is an
  // `isRefetchError` with the cached data still perfectly renderable —
  // swapping the whole editor for the error screen would toss the user's
  // session over a refetch hiccup.
  if ((draftQuery.isLoadingError && !createDraft.isPending) || createDraft.isError) {
    return (
      <div className="flex h-full items-center justify-center text-danger text-sm">
        {t('processDraft.loadError')}
      </div>
    );
  }

  if (!draftData || !processForFlow) {
    return <EndlessProcessLoader messageKey="processDraft.preparing" />;
  }

  return (
    <div ref={containerRef} tabIndex={-1} className="flex flex-col h-full w-full outline-none">
      <DraftEditorToolbar
        draft={draftData.draft}
        isSaving={saveStatus.isSaving}
        saveFailed={saveStatus.saveFailed}
        isPublishing={isPublishPending || reRequestAllSurveys.isPending}
        isDiscarding={discardDraft.isPending}
        issueCount={issues.issueCount}
        issuesOpen={issues.isShown}
        onToggleIssues={issues.toggleIssues}
        onShowIssues={issues.showIssues}
        issuesToggleRef={issues.issuesToggleRef}
        onBack={handleBack}
        onPublish={handlePublish}
        onDiscard={handleDiscard}
        onPublishAndReRequestAll={handlePublishAndReRequestAll}
      />
      {/*
       * The fatal guard above only fires when the INITIAL load failed; a
       * PERSISTENTLY failing background refetch (expired session, lasting
       * 5xx) still renders the stale cached draft while every optimistic
       * edit silently rolls back on the settle refetch. Surface that state
       * non-fatally: keep the editor usable for reading, but tell the user
       * their changes are not being saved. Gated on consecutive failures
       * (see `hasPersistentRefetchError`) so a single transient blip
       * doesn't flash it.
       */}
      {hasPersistentRefetchError && (
        <div
          role="alert"
          className="px-3 py-1.5 text-xs text-center text-warning-strong bg-warning-surface border-b border-warning-line"
        >
          {t('processDraft.refetchErrorBanner')}
        </div>
      )}
      <div className="flex min-h-0 flex-1">
        {issues.isShown && (
          <DraftIssuesSidebar
            groups={issues.groups}
            onGoToIssue={issues.goToIssue}
            onQuickFix={issues.runQuickFix}
            buttonRef={issues.keepFocusInEditor}
            onClose={issues.closeIssues}
          />
        )}
        <div className="relative min-w-0 flex-1">
          <DraftIssuesContext.Provider value={issues.contextValue}>
            {/*
             * Gate on `updatedAt`: the layout endpoint returns an empty shape
             * with `updatedAt: null` when no doc exists. Even though the loader
             * prefetches the seeded layout, the cache can transiently hold an
             * empty response (e.g. an observer refetched after discard, before
             * the loader's POST seeded the new doc). Mounting `ProcessFlowEditor`
             * during that window lets `useEditableFlow` init the PositionManager
             * with an empty saved map; `useSeedAlgoOnReady` then fires ELK and
             * persists its output, clobbering the publishedLayout-seeded
             * positions that arrive milliseconds later.
             *
             * Also gate on the assignee lookup: the swimlane build resolves
             * every lane and chip through it, so mounting against the empty
             * maps of a cold cache flashes raw-id lanes, an "Unassigned"
             * pile-up and "??" chips until the four lists land. The route
             * loader prefetches them, so this normally never shows.
             */}
            {!isCanvasReady ? (
              <EndlessProcessLoader messageKey="processDraft.preparing" />
            ) : (
              <ProcessFlowEditor
                process={processForFlow}
                nodePositions={nodePositions}
                onConnectSteps={undoableActions.onConnectSteps}
                onDisconnectSteps={undoableActions.onDisconnectSteps}
                onSavePositions={undoableActions.onSavePositions}
                onPaneContextMenu={handlePaneContextMenu}
                onNodeContextMenu={handleNodeContextMenu}
                onEdgeContextMenu={handleEdgeContextMenu}
                controlledSelectedNodeId={selectedStepId}
                onSelectedNodeChange={setSelectedStepId}
                renderDetail={renderDetail}
                draftEvents={draftEvents}
                contextMenu={contextMenuWrapper}
                bottomToolbar={bottomToolbar}
                revealRequest={issues.revealRequest}
                {...issues.markers}
                openAssignments={effectiveOpenAssignments}
                cursorMode={cursorMode}
                onDeleteNodes={handleDeleteNodes}
                swimlaneOrder={layoutData.swimlaneOrder}
                laneColors={layoutData.laneColors}
                onMoveSwimlaneUp={swimlaneActions.onMoveUp}
                onMoveSwimlaneDown={swimlaneActions.onMoveDown}
                edgeWaypoints={edgeWaypoints}
                onUpdateEdgeLayouts={undoableActions.onUpdateEdgeLayouts}
                assigneeLookup={assigneeLookup}
                entitySuggestions={entitySuggestions}
                notes={draftData?.notes}
                onUpdateNote={undoableActions.updateNote}
                onPlaceNote={undoableActions.placeNote}
                onRetargetNote={undoableActions.retargetNote}
                onDeleteNote={undoableActions.deleteNote}
                onAddFirstStep={handleAddFirstStep}
              />
            )}
          </DraftIssuesContext.Provider>
        </div>
      </div>
      {activeConflicts && (
        <DraftConflictDialog
          conflicts={activeConflicts}
          onCancel={handleDismissConflict}
          onDiscardAndRecreate={handleDiscardAndRecreate}
          onPublishAnyway={conflictSource === 'publish' ? handlePublishAnyway : null}
          isProcessing={isRecreatingDraft || publishDraft.isPending}
        />
      )}
      <MergeNodePickerDialog
        open={mergePickerSourceId !== null}
        onOpenChange={(open) => {
          if (!open) setMergePickerSourceId(null);
        }}
        sourceNodeId={mergePickerSourceId}
        steps={steps}
        draftAssignments={draftAssignments}
        entitySuggestions={entitySuggestions}
        assigneeLookup={assigneeLookup}
        stepNumberById={stepNumberByStableId}
        onMerge={undoableActions.mergeNodes}
        onMerged={setSelectedStepId}
      />
      {stepTemplates.dialogs}
    </div>
  );
}

export function DraftEditorPage({ processId }: Props) {
  return (
    <ReactFlowProvider>
      <DraftEditorInner processId={processId} />
    </ReactFlowProvider>
  );
}
